import React, { useCallback, useState, useRef, useEffect, useMemo } from "react";
import { motion } from "framer-motion";

import { useMedia } from "@rapid/contexts/MediaContext";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { FloatingHint } from "@rapid/components/FloatingHint";
import { formatTime, ThumbnailMode } from "@rapid/utils/videoEditing";
import { MAX_VIDEO_DURATION } from "@rapid/constants/media";
import { useAnalytics } from "@rapid/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@rapid/analytics/types";
import { ModalWithTitle } from "@scripts/react/components/RfModal/ModalWithTitle";
import { PlayPauseButton } from "@rapid/components/file/VideoControls/PlayPauseButton";
import { useVideoTimelinePreviews } from "@rapid/hooks/useVideoTimelinePreviews";
import { useSafeVideoElement } from "@rapid/hooks/useSafeBlobUrl";
import { LoadingOverlay } from "@rapid/components/file/Preview/Loading";
import { PreviewThumbnails } from "@rapid/components/file/Preview/PreviewThumbnails";

const THUMBNAIL_GENERATOR_OPTS = {
    size: 128,
    quality: 6,
    mode: ThumbnailMode.FAST,
    numPreviews: 8,
    defaultFramePreviews: Array(8).fill(undefined)
};

export const VideoTrimmer = () => {
    const { setCurrentTrimTimes, mediaItemToTrim, handleVideoTrim, rejectCurrentFile } = useMedia();
    const { track } = useAnalytics();
    const {
        createVideo: safeCreateVideo,
        cleanup: safeCleanup,
        manager: safeVideoManager
    } = useSafeVideoElement();

    const isOpen = useMemo(
        () => Boolean(mediaItemToTrim?.validation?.needsTrimming),
        [mediaItemToTrim?.validation?.needsTrimming]
    );

    const file = useMemo(() => mediaItemToTrim?.file, [mediaItemToTrim]);

    const totalFrames = useMemo(
        () => mediaItemToTrim?.metadata?.frame_count || 0,
        [mediaItemToTrim?.metadata]
    );
    const fileDuration = useMemo(
        () => mediaItemToTrim?.metadata?.videoMetadata?.duration || 0,
        [mediaItemToTrim?.metadata]
    );
    const keyframeOffset = useMemo(
        () => mediaItemToTrim?.metadata?.keyframe_offset || 0,
        [mediaItemToTrim?.metadata]
    );

    const isTrimming = useMemo(
        () => mediaItemToTrim?.metadata?.videoMetadata?.isTrimming || false,
        [mediaItemToTrim?.metadata]
    );

    const videoRef = useRef<HTMLVideoElement>(null);
    const timelineRef = useRef<HTMLDivElement>(null);
    const [duration, setDuration] = useState(0);
    const [startTime, setStartTime] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [isVideoReadyForSeeking, setIsVideoReadyForSeeking] = useState(false);

    // Local dragging state for immediate visual feedback
    const [dragStartTime, setDragStartTime] = useState<number | null>(null);
    const [touchStartTime, setTouchStartTime] = useState<number | null>(null);
    const [touchStartPosition, setTouchStartPosition] = useState<{ x: number; y: number } | null>(
        null
    );

    // Use dragging state if available, otherwise use actual startTime
    const displayStartTime = dragStartTime !== null ? dragStartTime : startTime;
    const endTime = Math.min(displayStartTime + MAX_VIDEO_DURATION, duration);

    // Update context with current trim times whenever they change (but not while dragging)
    useEffect(() => {
        if (duration > 0 && !isDragging) {
            const endTime = Math.min(startTime + MAX_VIDEO_DURATION, duration);

            setCurrentTrimTimes({ startTime, endTime });
        }
    }, [startTime, duration, setCurrentTrimTimes, isDragging]);

    useEffect(() => {
        if (!file) return;

        safeCreateVideo(file);
        setVideoUrl(safeVideoManager.current.blobManager.url);

        return () => safeCleanup();
    }, [file, safeCreateVideo, setVideoUrl, safeVideoManager, safeCleanup]);

    // Use the custom hook for preview generation
    const { orderedFramePreviews } = useVideoTimelinePreviews({
        mediaItem: mediaItemToTrim,
        totalFrames,
        fileDuration,
        keyframeOffset,
        isVideoReadyForSeeking,
        setIsVideoReadyForSeeking,
        videoRef,
        thumbnailGeneratorOpts: THUMBNAIL_GENERATOR_OPTS
    });

    const handleLoadedMetadata = () => {
        if (videoRef.current) {
            const videoDuration = videoRef.current.duration;
            setDuration(videoDuration);
            // Start with the first 20 seconds
            setStartTime(0);
        }
    };

    const handleTimeUpdate = useCallback(() => {
        if (videoRef.current) {
            setCurrentTime(videoRef.current.currentTime);
        }
    }, []);

    const handlePlayPause = useCallback(() => {
        setIsPlaying((prevIsPlaying) => {
            if (videoRef.current) {
                if (prevIsPlaying) {
                    videoRef.current.pause();
                } else {
                    videoRef.current.play();
                }
            }
            return !prevIsPlaying;
        });
    }, []);

    const handleSeek = useCallback((time: number) => {
        if (videoRef.current) {
            videoRef.current.currentTime = time;
            setCurrentTime(time);
        }
    }, []);

    const handleSelectionMouseDown = (e: React.MouseEvent) => {
        if (isTrimming) return; // Disable during trimming
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleSelectionTouchStart = (e: React.TouchEvent) => {
        if (isTrimming) return; // Disable during trimming
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isDragging || !timelineRef.current) return;

            const rect = timelineRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const percentage = Math.max(0, Math.min(x / rect.width, 1));
            const centerTime = percentage * duration;

            // Calculate new start time (center - 10s)
            const newStartTime = centerTime - MAX_VIDEO_DURATION / 2;
            const maxStartTime = Math.max(0, duration - MAX_VIDEO_DURATION);
            const clampedStartTime = Math.max(0, Math.min(newStartTime, maxStartTime));

            // Update local dragging state for immediate visual feedback
            setDragStartTime(clampedStartTime);
        },
        [isDragging, duration]
    );

    const handleTouchMove = useCallback(
        (e: TouchEvent) => {
            if (!isDragging || !timelineRef.current) return;

            // Prevent scrolling while dragging
            e.preventDefault();

            const touch = e.touches[0];
            if (!touch) return;

            const rect = timelineRef.current.getBoundingClientRect();
            const x = touch.clientX - rect.left;
            const percentage = Math.max(0, Math.min(x / rect.width, 1));
            const centerTime = percentage * duration;

            // Calculate new start time (center - 10s)
            const newStartTime = centerTime - MAX_VIDEO_DURATION / 2;
            const maxStartTime = Math.max(0, duration - MAX_VIDEO_DURATION);
            const clampedStartTime = Math.max(0, Math.min(newStartTime, maxStartTime));

            // Update local dragging state for immediate visual feedback
            setDragStartTime(clampedStartTime);
        },
        [isDragging, duration]
    );

    const handleMouseUp = useCallback(() => {
        if (isDragging && dragStartTime !== null) {
            // Apply the dragged position to actual state
            setStartTime(dragStartTime);
            handleSeek(dragStartTime);
            setDragStartTime(null); // Clear dragging state
        }
        setIsDragging(false);
    }, [isDragging, dragStartTime, handleSeek]);

    const handleTouchEnd = useCallback(() => {
        if (isDragging && dragStartTime !== null) {
            // Apply the dragged position to actual state
            setStartTime(dragStartTime);
            handleSeek(dragStartTime);
            setDragStartTime(null); // Clear dragging state
        }
        setIsDragging(false);
    }, [isDragging, dragStartTime, handleSeek]);

    const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!timelineRef.current || isDragging || isTrimming) return;

        const rect = timelineRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = Math.max(0, Math.min(x / rect.width, 1));
        const centerTime = percentage * duration;

        const maxStartTime = Math.max(0, duration - MAX_VIDEO_DURATION);
        const newStartTime = Math.max(
            0,
            Math.min(centerTime - MAX_VIDEO_DURATION / 2, maxStartTime)
        );

        setStartTime(newStartTime);
        setDragStartTime(null);
        setIsDragging(false);
        handleSeek(centerTime);
    };

    const handleTimelineTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        if (!timelineRef.current || isTrimming) return;

        const touch = e.touches[0];
        if (!touch) return;

        setTouchStartTime(Date.now());
        setTouchStartPosition({ x: touch.clientX, y: touch.clientY });
    };

    const handleTimelineTouch = (e: React.TouchEvent<HTMLDivElement>) => {
        if (!timelineRef.current || isDragging || isTrimming) return; // Disable during trimming

        // Prevent default to avoid triggering click events
        e.preventDefault();

        const touch = e.changedTouches[0]; // Use changedTouches for touchend
        if (!touch || !touchStartTime || !touchStartPosition) return;

        // Check if this was a quick tap (not a drag)
        const touchDuration = Date.now() - touchStartTime;
        const touchDistance = Math.sqrt(
            Math.pow(touch.clientX - touchStartPosition.x, 2) +
                Math.pow(touch.clientY - touchStartPosition.y, 2)
        );

        // Only seek if it was a quick tap with minimal movement
        if (touchDuration < 300 && touchDistance < 10) {
            const rect = timelineRef.current.getBoundingClientRect();
            const x = touch.clientX - rect.left;
            const percentage = x / rect.width;
            const clickTime = percentage * duration;

            handleSeek(Math.max(0, Math.min(clickTime, duration)));
        }

        // Reset touch tracking
        setTouchStartTime(null);
        setTouchStartPosition(null);
    };

    useEffect(() => {
        if (isDragging) {
            // Mouse events
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);

            // Touch events
            document.addEventListener("touchmove", handleTouchMove, { passive: false });
            document.addEventListener("touchend", handleTouchEnd);

            return () => {
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
                document.removeEventListener("touchmove", handleTouchMove);
                document.removeEventListener("touchend", handleTouchEnd);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

    // Handle modal close - reject the file
    const handleCloseModal = useCallback(() => {
        if (rejectCurrentFile) {
            rejectCurrentFile();
        }
    }, [rejectCurrentFile]);

    // Video trim handler
    const onClickTrimVideo = useCallback(async () => {
        try {
            track({ event: AnalyticsFlowEvent.TRIM_STARTED });
            handleVideoTrim();
        } catch (error) {
            console.error("🎬 [VIDEO_TRIMMER] Video trim failed:", error);
        }
    }, [handleVideoTrim, track]);

    return (
        <ModalWithTitle
            isOpen={isOpen}
            onClose={handleCloseModal}
            onSubmit={onClickTrimVideo}
            submitButtonText={
                isTrimming ? (
                    "Trimming..."
                ) : (
                    <>
                        <i className="far fa-scissors" /> Upload Trimmed Video
                    </>
                )
            }
            submitButtonProps={{
                loading: isTrimming,
                loadingText: "Trimming...",
                disabled: isTrimming
            }}
            title={`Trim Video to ${MAX_VIDEO_DURATION} Seconds`}
            icon="fad fa-cut"
            size="max"
            scrollInsideModal={true}
            panelClassName="h-full"
            overlayClassName="backdrop-blur-sm"
            className="visualArea bg-gray-50"
            allowClickPropagation
            allowMouseUpPropagation
        >
            {/* Video Preview - made smaller to accommodate controls */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15, ease: "easeInOut" }}
                className="fileCarouselWindow flex-1 overflow-hidden"
            >
                <div className="filePreview">
                    <div className="floatingHint">
                        <FloatingHint
                            isVisible={!isTrimming}
                            message={
                                isTrimming
                                    ? "Trimming..."
                                    : `Drag the selection window (${MAX_VIDEO_DURATION}s) to choose which part of your
                                    video to upload.`
                            }
                        />
                    </div>
                    <LoadingOverlay show={isTrimming} loadingText="Trimming..." />
                    {videoUrl && (
                        <video
                            ref={videoRef}
                            src={videoUrl}
                            className={twClassNames(
                                "absolute left-0 top-0 h-full w-full object-contain transition-all duration-300"
                            )}
                            onLoadedMetadata={handleLoadedMetadata}
                            onTimeUpdate={handleTimeUpdate}
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            muted
                            playsInline
                        />
                    )}
                </div>

                {/* Timeline Container - matches VideoControls styling */}
                <div
                    className={twClassNames(
                        "videoTimelineContainer",
                        isTrimming && "pointer-events-none opacity-50"
                    )}
                    style={{ touchAction: isTrimming ? "auto" : "none" }}
                >
                    <PlayPauseButton
                        isPlaying={isPlaying}
                        onClick={handlePlayPause}
                        disabled={isTrimming}
                    />

                    {/* Timeline */}
                    <div
                        ref={timelineRef}
                        className={twClassNames(
                            "videoTimeline relative select-none rounded-md transition-all duration-300",
                            isTrimming ? "cursor-not-allowed" : "cursor-pointer"
                        )}
                        onClick={handleTimelineClick}
                        onTouchStart={handleTimelineTouchStart}
                        onTouchEnd={handleTimelineTouch}
                    >
                        {/* Preview thumbnails */}
                        <PreviewThumbnails previews={orderedFramePreviews} />

                        {/* Opaque overlay for non-selected areas */}
                        {orderedFramePreviews.length > 0 && (
                            <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden rounded-md">
                                {/* Left opaque area */}
                                <div
                                    className="absolute inset-y-0 left-0 bg-black/30"
                                    style={{ width: `${(displayStartTime / duration) * 100}%` }}
                                />
                                {/* Right opaque area */}
                                <div
                                    className="absolute inset-y-0 right-0 bg-black/30"
                                    style={{ width: `${((duration - endTime) / duration) * 100}%` }}
                                />
                            </div>
                        )}

                        {/* Clear 20s selection window with border */}
                        <div
                            className={twClassNames(
                                "bg-brand/30 absolute bottom-0 top-0 z-20 rounded-md border-b-4 border-l-[16px] border-r-[16px] border-t-4 border-brand shadow-md ring-1 ring-inset ring-white/60 transition-all duration-300",
                                isTrimming
                                    ? "cursor-not-allowed opacity-75"
                                    : "cursor-grab active:cursor-grabbing"
                            )}
                            style={{
                                left: `${(displayStartTime / duration) * 100}%`,
                                width: `${(MAX_VIDEO_DURATION / duration) * 100}%`
                            }}
                            onMouseDown={handleSelectionMouseDown}
                            onTouchStart={handleSelectionTouchStart}
                        />

                        {/* Current time indicator */}
                        <div
                            className="currentTimeIndicator absolute inset-y-1/2 h-20 -translate-y-1/2"
                            style={{ left: `${(currentTime / duration) * 100}%` }}
                        />

                        {/* Timestamps above selection */}
                        <div
                            className="timestampContainer -top-6"
                            style={{
                                left: `${((displayStartTime + MAX_VIDEO_DURATION / 2) / duration) * 100}%`,
                                transform: "translateX(-50%)"
                            }}
                        >
                            <span className="timestamp">
                                {formatTime(displayStartTime)} - {formatTime(endTime)}
                            </span>
                        </div>
                    </div>
                </div>
            </motion.div>
        </ModalWithTitle>
    );
};
